import { Component, inject, OnInit } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { StepPanelEnums } from 'src/app/enum/step-panel-enums';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { OrderDetailSelectionTableModel } from 'src/app/models/tables/preliminaries/order-detail-selection-table.model';
import { OrderSelectionTableModel } from 'src/app/models/tables/preliminaries/order-selection-table.model';
import { OrderDetailService } from 'src/app/services/preliminaries/order-detail.service';
import { OrderService } from 'src/app/services/preliminaries/order.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-service-manual-order',
  templateUrl: './service-manual-order.component.html',
  styleUrls: ['./service-manual-order.component.scss'],
  standalone: false
})
export class ServiceManualOrderComponent implements OnInit {

  public readonly config = inject(DynamicDialogConfig);
  private readonly orderDetailService = inject(OrderDetailService);
  private readonly orderService = inject(OrderService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  ascendant: boolean = true;
  ascendantDetail: boolean = true;
  branchId: number = 0;
  columnSort: string | null = null;
  columnSortDetail: string | null = null;
  currentStep: number = 1;
  data: PagedResponseModel<OrderSelectionTableModel> =
    new PagedResponseModel<OrderSelectionTableModel>();
  dataDetail: PagedResponseModel<OrderDetailSelectionTableModel> =
    new PagedResponseModel<OrderDetailSelectionTableModel>();
  filters: ColumnResponseModel[] = [];
  filtersDetail: ColumnResponseModel[] = [];
  header: ColumnResponseModel[] = [];
  headerDetail: ColumnResponseModel[] = [];
  limit: number = 10;
  limitDetail: number = 10;
  loading: boolean = false;
  orderIdFromConfig: number = 0;
  orderIdSelected: number = 0;
  page: number = 1;
  pageDetail: number = 1;
  readonly stepPanel = StepPanelEnums;
  subscription!: Subscription;

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$.subscribe(async (inputs: any) => {
      this.branchId = inputs.branchId;
      this.orderIdFromConfig = inputs.orderId ?? 0;
      if (this.orderIdFromConfig > 0) {
        this.currentStep = StepPanelEnums.PanelTwo;
        this.loading = true;
        this.orderIdSelected = this.orderIdFromConfig;
        await this.getDataForStep(StepPanelEnums.PanelTwo);
        setTimeout(() => this.loading = false, 300);
      } else {
        this.currentStep = StepPanelEnums.PanelOne;
        this.loading = true;
        await this.getDataForStep(StepPanelEnums.PanelOne);
        setTimeout(() => this.loading = false, 300);
      }
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async getColumnsForOrderSelection() {
    try {
      if (this.header.length === 0) {
        const response = await firstValueFrom(this.orderService.getColumnsForOrderSelection());
        this.header = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de pedidos.', error);
    }
  }

  async getColumnsForOrderDetailSelection() {
    try {
      if (this.headerDetail.length === 0) {
        const response = await firstValueFrom(this.orderDetailService.getColumnsForOrderDetailSelection());
        this.headerDetail = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de pedido detalle.', error);
    }
  }

  async getDataForStep(step: StepPanelEnums) {
    try {
      const actions: Record<number, () => Promise<any>> = {
        [StepPanelEnums.PanelOne]: () => Promise.all([
          this.getColumnsForOrderSelection(),
          this.getOrdersForSelection()
        ]),
        [StepPanelEnums.PanelTwo]: () => Promise.all([
          this.getColumnsForOrderDetailSelection(),
          this.getOrderDetailsForSelection()
        ])
      };
      if (actions[step]) {
        await actions[step]();
      }
    } catch (error) {
      console.error(`Error cargando datos del ${StepPanelEnums[step]}:`, error);
    }
  }

  async getOrdersForSelection() {
    try {
      const response = await firstValueFrom(this.orderService.getOrdersForSelection(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters, this.branchId, this.orderIdFromConfig, true
      ));
      this.data = response;
    } catch (error) {
      console.error('Error al obtener la lista de los pedidos.', error);
    }
  }

  async getOrderDetailsForSelection() {
    try {
      const response = await firstValueFrom(this.orderDetailService.getOrderDetailsForSelection(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters, this.orderIdSelected
      ));
      this.dataDetail = response;
    } catch (error) {
      console.error('Error al obtener la lista de los pedidos.', error);
    }
  }

  async goToStep(step: StepPanelEnums, activateCallback: (step: number) => void) {
    if (step === StepPanelEnums.PanelTwo && this.orderIdSelected === 0) {
      this.toastHandlerService.showToast('warning', 'Por favor, seleccione un pedido.');
      return;
    }

    if (step === StepPanelEnums.PanelOne) {
      this.orderIdSelected = this.orderIdFromConfig;
    }

    await this.getDataForStep(step);
    this.currentStep = step;
    activateCallback(step);
  }

  async onFilterChange(event: { filters: any[] }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    const cleanFilters = this.removeDuplicates(filters);
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.filters = cleanFilters;
        this.filters.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelOne));
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.filtersDetail = cleanFilters;
        this.filtersDetail.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelTwo));
        break;
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.page = event.page;
        this.limit = event.pageSize;
        this.columnSort = event.sortField || null;
        this.ascendant = event.sortOrder === 1;
        await this.getOrdersForSelection();
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.pageDetail = event.page;
        this.limitDetail = event.pageSize;
        this.columnSortDetail = event.sortField || null;
        this.ascendantDetail = event.sortOrder === 1;
        await this.getOrderDetailsForSelection();
        break;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  processFilter(filter: any, step: StepPanelEnums) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    const isInvalid = valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0;
    if (isInvalid) {
      if (step == StepPanelEnums.PanelOne) {
        this.filters = this.filters.filter(f => f.propertyName !== propertyName);
      } else if (step == StepPanelEnums.PanelTwo) {
        this.filtersDetail = this.filtersDetail.filter(f => f.propertyName !== propertyName);
      }
    } else {
      const list = step === StepPanelEnums.PanelOne ? this.filters : this.filtersDetail;
      const existingFilter = list.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  selectionChange({ selectedItem }: { selectedItem?: OrderSelectionTableModel }) {
    const { orderId = 0 } = selectedItem ?? {};
    this.orderIdSelected = orderId;
  }
}
